<template>
    <form
        ref="form"
        @submit.prevent="submit"
        :action="formRoute"
        method="POST"
        class="relative h-full"
    >
        <page-header
            :submit-button-text="data ? 'Update' : 'Create'"
            :show-submit-button="true"
            :submit-button-disabled="submitButtonDisabled || !editable"
            submit-button-type="submit"

            :can-delete="canDelete"
            :delete-route="deleteRoute"
            :delete-value="deleteValue"

            :can-create="canCreate"
            :create-route="createRoute"
            :create-value="createValue"

            :index-route="indexRoute"
            :index-value="indexValue"
            :title="title"
            :subtitle="subtitle"

            :show-preview-button="false"

            :errors="errors"
            :is-slide-over="isSlideOver"

            :version-id="versionId"
            :init-version-id="initVersionId"
            :last-updated-by="lastUpdatedBy"
        />

        <input-rich-text
            name="editor_note"
            label="Editor's note"
            v-model="form.editor_note"
            :language="form.language"
            :error="errors.editor_note ? errors.editor_note[0] : null"
            :maxlength="60000"
        />
        <p class="my-1 text-xs text-gray-600 mb-3">The note is for internal use only and won’t be displayed on the site</p>

        <input-text
            name="name"
            label="* Name"
            v-model="form.name"
            :error="errors.name ? errors.name[0] : null"
            :maxlength="255"
        />

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">* Casino</p>
            <reference
                create-text="Create new casino and link casino to this page"
                link-text="Link an existing casino"
                :language="form.language"
                v-model="form.casino"
                model-data="Casino"
                :should-open-new-window="true"
                :error="errors.casino_id ? errors.casino_id[0] : null"
                :creatable="false"
                :query="{
                    'casinoReview.status_id': 1
                }"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">* Bonus</p>
            <reference
                create-text="Create new bonus and link bonus to this page"
                link-text="Link an existing bonus"
                :language="form.language"
                v-model="form.bonus"
                model-data="Bonus"
                :should-open-new-window="true"
                :error="errors.bonus_id ? errors.bonus_id[0] : null"
                :creatable="false"
                :query="{
                    'casino_id': form.casino_id
                }"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>

        <Select
            label="* Label Text Type"
            name="label_text_type"
            class="mt-3"
            v-model="form.label_text_type"
            :options="[
                { value: '', name: ''},
                { value: 'free', name: 'Free Text'},
                { value: 'predefined', name: 'Predefined'},
            ]"
            :error="errors.label_text_type ? errors.label_text_type[0] : null"
            @input="labelTextTypeChanged"
        />

        <input-text
            name="label_text"
            label="* Label Free Text"
            class="mt-3"
            v-model="form.label_text"
            v-if="(form.label_text_type == 'free')"
            :error="errors.label_text ? errors.label_text[0] : null"
            :maxlength="255"
        />

        <Select
            label="* Label Predefined Text"
            name="label_key"
            class="mt-3"
            v-if="(form.label_text_type == 'predefined')"
            v-model="form.label_key"
            :options="[
                { value: '', name: ''},
                { value: 'ribbon.communityTopChoice', name: 'Community Top Choice' },
                { value: 'ribbon.editorsTopChoice', name: 'Editors Top Choice'},
                { value: 'ribbon.playersChoiceOfTheMonth', name: 'Players Choice of the Month' },
                { value: 'ribbon.mostAwardedCasinoOfYear', name: 'Most Awarded Casino of year'},
                { value: 'ribbon.newestCasinoReview', name: 'Newest Casino Review' },
                { value: 'ribbon.trustCategoryWinner', name: 'Trust Category Winner' },
                { value: 'ribbon.mostVisitedCasino', name: 'Most visited Casino'},
                { value: 'ribbon.latestCasinoBonus', name: 'Latest Casino Bonus' },
                { value: 'ribbon.highestBonusPercentage', name: 'Highest Bonus Percentage'},
                { value: 'ribbon.mostPopularWelcomeBonus', name: 'Most Popular Welcome Bonus' },
                { value: 'ribbon.mostUsedFreeSpinBonus', name: 'Most used Free Spin Bonus' },
                { value: 'ribbon.mostGenerousNoDepositBonus', name: 'Most generous No Deposit Bonus' },
                { value: 'ribbon.playersFavoriteNoDepositBonus', name: 'Players’ Favorite No Deposit Bonus'},
                { value: 'ribbon.highestCashbackOffer', name: 'Highest Cashback Offer' },
                { value: 'ribbon.perfectForHighRollers', name: 'Perfect for High Rollers'},
                { value: 'ribbon.exclusiveOffer', name: 'Exclusive Offer' }
            ]"
            :error="errors.label_key ? errors.label_key[0] : null"
        />

        <Select
            label="* Label Color"
            name="label_color"
            class="mt-3"
            v-if="(form.label_text_type)"
            v-model="form.label_color"
            :options="[
                { value: '', name: ''},
                { value: 'cta',     name: 'Base CTA' },
                { value: 'purple',  name: 'Purple '},
                { value: 'green',   name: 'Green' },
                { value: 'cyan',    name: 'Cyan'},
                { value: 'amber',   name: 'Amber' },
                { value: 'blue',    name: 'Blue' }
            ]"
            :error="errors.label_color ? errors.label_color[0] : null"
        />

        <div class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3">
            <p class="text-gray-700 mb-4">* Language</p>
            <reference
                create-text="Create new language and link language to this page"
                link-text="Link an existing language"
                :creatable="false"
                :editable="false"
                :error="errors.language_id ? errors.language_id[0] : null"
                v-model="form.language"
                model-data="Site Language"
                :disabled="true"
            />
        </div>

        <input type="hidden" name="_token" :value="csrf" />
        <input v-if="data" type="hidden" name="_method" value="patch" />
    </form>
</template>

<script>
import crudForm from "../mixins/crudForm";
export default {
    name: "StickyCTAForm",
    mixins: [crudForm],
    data() {
        return {
            form: {
                editor_note: this.getFormData("editor_note"),
                name: this.getFormData("name"),
                casino: this.getFormData("casino"),
                casino_id: this.getFormData("casino_id"),
                bonus: this.getFormData("bonus"),
                bonus_id: this.getFormData("bonus_id"),
                label_text_type: this.getFormData("label_text_type"),
                label_text: this.getFormData("label_text"),
                label_key: this.getFormData("label_key"),
                label_color: this.getFormData("label_color")
            },
        };
    },
    methods: {
        labelTextTypeChanged() {
            this.form.label_text = null;
            this.form.label_key = null;
            this.form.label_color = null;
        }
    },
    watch: {
        "form.casino": function (newValue) {
            this.form.casino_id = newValue ? newValue.id : null;
            if(!newValue) {
                this.form.bonus = null;
                this.form.bonus_id = null;
            }
        },
        "form.bonus": function (newValue) {
            this.form.bonus_id = newValue ? newValue.id : null;
        }
    },
};
</script>
